import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { requestCommunityInformationReport } from '../../../../../actions/reportsActions'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { deserialize } from '../../../../../utility/serialization'
import { numbersToPercentage } from '../../../../../utility/numbersHelper'
import GenderBreakdownPie from '../../../../common/GenderBreakdownPie/GenderBreakdownPie'
import Loader from '../../../../common/loader/Loader'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './CommunityInformationWidget.module.scss'

const GroupPanel = ({ title, group, percentage }) => {
  const { t } = useTranslation()

  return (
    <div className={classnames('d-flex flex-column', businessDashboardClasses.sectionWrapper, classes.sectionWrapper)}>
      <div className='d-flex flex-column align-items-center'>
        <h3 className={classes.groupTitle}>
          {title} {percentage}
        </h3>
        <h4 className={classes.groupCount}>{group.count}</h4>
      </div>

      <div>
        <span className={classes.label}>{t('common.golf.skillLevel')}</span>
        <div className={classes.skillLevelRow}>
          <SkillLevelBox
            label={t('common.golf.beginner')}
            subLabel={t('dashboard.business.communityInformation.communityInformationWidget.skillLevelBeginnerRange')}
            count={group.skillLevels.beginner} total={group.count}
          />
          <SkillLevelBox
            label={t('common.golf.intermediate')}
            subLabel={t('dashboard.business.communityInformation.communityInformationWidget.skillLevelIntermediateRange')}
            count={group.skillLevels.intermediate} total={group.count}
          />
          <SkillLevelBox
            label={t('common.golf.advanced')}
            subLabel={t('dashboard.business.communityInformation.communityInformationWidget.skillLevelAdvancedRange')}
            count={group.skillLevels.advanced} total={group.count}
          />
        </div>
      </div>

      <div className='d-flex flex-column flex-grow-1 flex-shrink-0 min-h-0'>
        <div className='d-flex justify-content-between align-items-center'>
          <span className={classes.label}>{t('dashboard.business.communityInformation.communityInformationWidget.genderAndAge')}</span>
          <span className={classes.averageAge}>
            {group.count > 0 ? t('dashboard.business.communityInformation.communityInformationWidget.averageAge', { age: group.averageAge }) : '---'}
          </span>
        </div>
        <div className='mt-3 px-5 flex-grow-1 flex-shrink-0 min-h-0'>
          <GenderBreakdownPie
            male={group.genders.male}
            female={group.genders.female}
            other={group.genders.other}
          />
        </div>
      </div>
    </div>
  )
}

GroupPanel.propTypes = {
  title: PropTypes.string.isRequired,
  group: PropTypes.shape({
    count: PropTypes.number.isRequired,
    averageAge: PropTypes.number.isRequired,
    skillLevels: PropTypes.shape({
      beginner: PropTypes.number.isRequired,
      intermediate: PropTypes.number.isRequired,
      advanced: PropTypes.number.isRequired,
    }).isRequired,
    genders: PropTypes.shape({
      male: PropTypes.number.isRequired,
      female: PropTypes.number.isRequired,
      other: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  percentage: PropTypes.string.isRequired,
}

const SkillLevelBox = ({ label, subLabel, count, total }) => (
  <div className={classes.skillBox}>
    <span className={classes.skillPercent}>{total > 0 ? numbersToPercentage(count, total, true) : '0%'}</span>
    <span className={classes.label}>{label}</span>
    <span className={classes.skillSubLabel}>{subLabel}</span>
  </div>
)

SkillLevelBox.propTypes = {
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
}

const SCOPES = { ACTIVE: 'active', ALL_TIME: 'all_time' }

const EMPTY_GROUP = {
  count: 0,
  averageAge: 0,
  skillLevels: { beginner: 0, intermediate: 0, advanced: 0 },
  genders: { male: 0, female: 0, other: 0 },
}

const CommunityInformationWidget = () => {
  const { t } = useTranslation()
  const community = useSelector(state => state.user.current_user.communities[0])
  const [scope, setScope] = useState(SCOPES.ACTIVE)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)

  const total = data?.totalStudents ?? 0
  const juniors = data?.juniors ?? EMPTY_GROUP
  const adults = data?.adults ?? EMPTY_GROUP
  const classifiedTotal = juniors.count + adults.count
  const juniorsPercentage = classifiedTotal > 0 ? numbersToPercentage(juniors.count, classifiedTotal, true) : '0%'
  const adultsPercentage = classifiedTotal > 0 ? numbersToPercentage(adults.count, classifiedTotal, true) : '0%'

  const handleTabChange = (isRight) => {
    setScope(isRight ? SCOPES.ALL_TIME : SCOPES.ACTIVE)
  }

  useEffect(() => {
    setIsLoading(true)

    requestCommunityInformationReport({ communityId: community?.id, scope })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoading(false)
      })
      .catch(() => toast.error(t(COMMON_ERRORS[500])))
  }, [community?.id, scope])

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={businessDashboardClasses.titleWrapper}>
        <h2 className={businessDashboardClasses.title}>{t('dashboard.business.communityInformation.communityInformationWidget.title')}</h2>
      </div>

      <div>
        <button
          className={classnames('btn-tab', { active: scope === SCOPES.ACTIVE })}
          onClick={() => handleTabChange(false)}
        >
          {t('dashboard.business.communityInformation.communityInformationWidget.tabActive')}
        </button>
        <button
          className={classnames('btn-tab ml-3', { active: scope === SCOPES.ALL_TIME })}
          onClick={() => handleTabChange(true)}
        >
          {t('dashboard.business.communityInformation.communityInformationWidget.tabAllTime')}
        </button>
      </div>

      {isLoading ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>{t('dashboard.business.communityInformation.communityInformationWidget.loadingData')}</Loader>
        </div>
      ) : (
        <>
          <div className={classnames('d-flex justify-content-center', businessDashboardClasses.sectionWrapper)}>
            <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
              <span className={businessDashboardClasses.value}>{total}</span>
              <span className={businessDashboardClasses.topLabel}>
                {t('dashboard.business.communityInformation.communityInformationWidget.totalStudents')}
              </span>
            </div>
          </div>

          <div className='d-flex justify-content-between my-2'>
            <span className={classes.label}>
              {t('dashboard.business.communityInformation.communityInformationWidget.juniorsWithPercentage', { percentage: juniorsPercentage })}
            </span>
            <span className={classes.label}>
              {t('dashboard.business.communityInformation.communityInformationWidget.adultsWithPercentage', { percentage: adultsPercentage })}
            </span>
          </div>

          <div className={classnames('mb-3 progress', classes.progressBackground)}>
            <div
              className={classes.progressBar}
              role='progressbar'
              style={{ width: juniorsPercentage }}
              aria-valuemin='0'
              aria-valuemax='100'
            />
          </div>

          <div className={classes.groupsRow}>
            <GroupPanel
              title={t('dashboard.business.communityInformation.communityInformationWidget.juniors')}
              group={juniors} percentage={juniorsPercentage}
            />
            <GroupPanel
              title={t('dashboard.business.communityInformation.communityInformationWidget.adults')}
              group={adults} percentage={adultsPercentage}
            />
          </div>
        </>
      )}
    </section>
  )
}

export default CommunityInformationWidget
